/**
 * Wishlist Manager - Core functionality for wishlist feature
 * Handles both guest (localStorage) and authenticated (metafields) users
 */

class WishlistManager {
  constructor() {
    this.STORAGE_KEY = 'shopify_wishlist_v2'; // Updated to v2 for new format
    this.METAFIELD_NAMESPACE = 'custom';
    this.METAFIELD_KEY = 'wishlist';
    this.MAX_ITEMS = 100;
    
    this.wishlist = []; // Will store objects: {id, handle, title}
    this.isAuthenticated = false;
    this.customerId = null;
    this.isInitialized = false;
    this.listeners = new Map();
    
    this.init();
  }

  /**
   * Initialize wishlist manager
   */
  async init() {
    try {
      // Check if user is logged in
      this.checkAuthentication();
      
      // Load wishlist based on authentication status
      if (this.isAuthenticated) {
        await this.loadFromMetafields();
        // Migrate guest wishlist if exists
        await this.migrateGuestWishlist();
      } else {
        this.loadFromLocalStorage();
      }
      
      this.isInitialized = true;
      this.emit('initialized', this.wishlist);
      
      // Set up periodic sync for authenticated users
      if (this.isAuthenticated) {
        this.setupPeriodicSync();
      }
    } catch (error) {
      console.error('Failed to initialize wishlist:', error);
      // Fallback to localStorage
      this.loadFromLocalStorage();
    }
  }

  /**
   * Check if user is authenticated
   */
  checkAuthentication() {
    // Check for customer data in the page
    const customerMeta = document.querySelector('meta[name="customer-id"]');
    if (customerMeta) {
      this.customerId = customerMeta.content;
      this.isAuthenticated = true;
      console.log('Authentication detected via meta tag:', this.customerId);
    } else if (window.ShopifyAnalytics?.meta?.page?.customerId) {
      this.customerId = window.ShopifyAnalytics.meta.page.customerId;
      this.isAuthenticated = true;
      console.log('Authentication detected via ShopifyAnalytics:', this.customerId);
    } else if (window.customerId) {
      // Check for global customerId variable
      this.customerId = window.customerId;
      this.isAuthenticated = true;
      console.log('Authentication detected via window.customerId:', this.customerId);
    } else {
      // Check if __st cookie exists (Shopify customer token)
      this.isAuthenticated = document.cookie.includes('_shopify_customer_token');
      if (this.isAuthenticated) {
        console.log('Authentication detected via cookie, but no customer ID available');
        // Try to extract from DOM or wait for it
        this.findCustomerId();
      } else {
        console.log('No authentication detected');
      }
    }
  }

  /**
   * Try to find customer ID from various sources
   */
  findCustomerId() {
    // Check common locations for customer ID
    const sources = [
      () => document.querySelector('[data-customer-id]')?.dataset.customerId,
      () => document.querySelector('.customer-id')?.textContent,
      () => window.__st?.cid,
      () => window.meta?.page?.customerId
    ];

    for (const source of sources) {
      try {
        const id = source();
        if (id) {
          this.customerId = id;
          console.log('Found customer ID:', id);
          return;
        }
      } catch (e) {
        // Continue to next source
      }
    }
  }

  /**
   * Load wishlist from localStorage
   */
  loadFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.wishlist = data.items || [];
      }
    } catch (error) {
      console.error('Failed to load wishlist from localStorage:', error);
      this.wishlist = [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveToLocalStorage() {
    try {
      const data = {
        items: this.wishlist,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save wishlist to localStorage:', error);
    }
  }

  /**
   * Load wishlist from customer metafields using Customer Account API
   */
  async loadFromMetafields() {
    if (!this.isAuthenticated) {
      this.loadFromLocalStorage();
      return;
    }

    try {
      const query = `
        query getCustomerWishlist {
          customer {
            metafield(namespace: "custom", key: "wishlist") {
              value
            }
          }
        }
      `;

      const response = await fetch('/account/customer/api/2024-01/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data?.customer?.metafield?.value) {
          // Parse the JSON value from metafield
          this.wishlist = JSON.parse(data.data.customer.metafield.value);
          console.log('Loaded wishlist from metafields:', this.wishlist);
          // Also save to localStorage for faster access
          this.saveToLocalStorage();
        } else {
          // No metafield data, load from localStorage
          this.loadFromLocalStorage();
        }
      } else {
        throw new Error('Failed to fetch from Customer Account API');
      }
    } catch (error) {
      console.error('Failed to load wishlist from metafields:', error);
      // Fallback to localStorage
      this.loadFromLocalStorage();
    }
  }

  /**
   * Save wishlist to customer metafields using Customer Account API
   */
  async saveToMetafields() {
    if (!this.isAuthenticated) {
      console.log('Cannot save to metafields: user not authenticated');
      return;
    }

    console.log('Attempting to save wishlist to metafields:', {
      itemCount: this.wishlist.length,
      customerId: this.customerId
    });

    try {
      // First, let's try a simpler mutation that doesn't require CustomerUpdateInput
      const mutation = `
        mutation updateMetafield($metafields: [MetafieldInput!]!) {
          metafieldsSet(metafields: $metafields) {
            metafields {
              namespace
              key
              value
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        metafields: [{
          namespace: "custom",
          key: "wishlist",
          value: JSON.stringify(this.wishlist),
          type: "json",
          ownerId: `gid://shopify/Customer/${this.customerId}`
        }]
      };

      console.log('Sending GraphQL mutation with variables:', JSON.stringify(variables, null, 2));

      const response = await fetch('/account/customer/api/2024-01/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: mutation, 
          variables 
        })
      });

      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (response.ok) {
        const data = JSON.parse(responseText);
        
        if (data.errors) {
          console.error('GraphQL errors:', data.errors);
          // Try fallback method with different mutation
          await this.saveToMetafieldsFallback();
          return;
        }
        
        if (data.data?.metafieldsSet?.userErrors?.length > 0) {
          console.error('Metafield user errors:', data.data.metafieldsSet.userErrors);
          // Try fallback method
          await this.saveToMetafieldsFallback();
          return;
        }
        
        console.log('Successfully saved wishlist to metafields:', data);
      } else {
        console.error(`HTTP error! status: ${response.status}, response: ${responseText}`);
        // Try fallback method
        await this.saveToMetafieldsFallback();
      }
    } catch (error) {
      console.error('Failed to save wishlist to metafields:', error);
      // Try fallback method
      try {
        await this.saveToMetafieldsFallback();
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        // Always save to localStorage as backup
        this.saveToLocalStorage();
      }
    }
  }

  /**
   * Fallback method to save metafields using AJAX API
   */
  async saveToMetafieldsFallback() {
    console.log('Trying fallback method for saving metafields via AJAX');
    
    try {
      // Use the simpler AJAX approach that works with Shopify's built-in customer endpoints
      const formData = new FormData();
      formData.append('customer[metafields][custom.wishlist]', JSON.stringify(this.wishlist));
      
      const response = await fetch('/account', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (response.ok) {
        console.log('Successfully saved wishlist to metafields via AJAX');
      } else {
        // Try one more approach using the admin API proxy if available
        await this.saveViaAdminProxy();
      }
    } catch (error) {
      console.error('AJAX save failed:', error);
      // Try admin proxy as last resort
      try {
        await this.saveViaAdminProxy();
      } catch (proxyError) {
        console.error('Admin proxy also failed:', proxyError);
        throw proxyError;
      }
    }
  }

  /**
   * Try to save via admin API proxy if available
   */
  async saveViaAdminProxy() {
    console.log('Trying to save via admin API proxy');
    
    try {
      const response = await fetch('/apps/wishlist/metafield', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: this.customerId,
          wishlist: this.wishlist
        })
      });

      if (!response.ok) {
        throw new Error('Admin proxy not available');
      }
      
      console.log('Successfully saved via admin proxy');
    } catch (error) {
      console.error('Admin proxy save failed:', error);
      throw error;
    }
  }

  /**
   * Add product to wishlist
   * @param {string} productId - Product ID
   * @param {string} productHandle - Product handle for fetching
   * @param {string} productTitle - Product title for display
   */
  async add(productId, productHandle = null, productTitle = null) {
    if (!productId) return false;

    // Remove 'gid://shopify/Product/' prefix if present
    productId = this.normalizeProductId(productId);

    // Check if already in wishlist
    if (this.contains(productId)) {
      return false;
    }

    // Check max items limit
    if (this.wishlist.length >= this.MAX_ITEMS) {
      this.emit('error', { message: 'Wishlist is full. Maximum ' + this.MAX_ITEMS + ' items allowed.' });
      return false;
    }

    // Create product object
    const productData = {
      id: productId,
      handle: productHandle,
      title: productTitle,
      addedAt: new Date().toISOString()
    };

    // Add to wishlist
    this.wishlist.push(productData);

    // Save based on authentication
    if (this.isAuthenticated) {
      await this.saveToMetafields();
    } else {
      this.saveToLocalStorage();
    }

    // Emit event
    this.emit('added', { productId, productData, wishlist: this.wishlist });
    this.trackAnalytics('wishlist_add', { product_id: productId, product_handle: productHandle });

    return true;
  }

  /**
   * Remove product from wishlist
   */
  async remove(productId) {
    if (!productId) return false;

    productId = this.normalizeProductId(productId);

    const index = this.wishlist.findIndex(item => 
      typeof item === 'object' ? item.id === productId : item === productId
    );
    if (index === -1) return false;

    // Remove from wishlist
    this.wishlist.splice(index, 1);

    // Save based on authentication
    if (this.isAuthenticated) {
      await this.saveToMetafields();
    } else {
      this.saveToLocalStorage();
    }

    // Emit event
    this.emit('removed', { productId, wishlist: this.wishlist });
    this.trackAnalytics('wishlist_remove', { product_id: productId });

    return true;
  }

  /**
   * Toggle product in wishlist
   */
  async toggle(productId, productHandle = null, productTitle = null) {
    if (this.contains(productId)) {
      return await this.remove(productId);
    } else {
      return await this.add(productId, productHandle, productTitle);
    }
  }

  /**
   * Check if product is in wishlist
   */
  contains(productId) {
    productId = this.normalizeProductId(productId);
    return this.wishlist.some(item => 
      typeof item === 'object' ? item.id === productId : item === productId
    );
  }

  /**
   * Get all wishlist items
   */
  getItems() {
    return [...this.wishlist];
  }

  /**
   * Get wishlist count
   */
  getCount() {
    return this.wishlist.length;
  }

  /**
   * Clear wishlist
   */
  async clear() {
    this.wishlist = [];

    if (this.isAuthenticated) {
      await this.saveToMetafields();
    } else {
      this.saveToLocalStorage();
    }

    this.emit('cleared', { wishlist: this.wishlist });
    this.trackAnalytics('wishlist_clear');
  }

  /**
   * Migrate guest wishlist to customer account
   */
  async migrateGuestWishlist() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data = JSON.parse(stored);
      const guestItems = data.items || [];

      if (guestItems.length === 0) return;

      // Merge guest items with existing wishlist (avoid duplicates by ID)
      const existingIds = new Set(this.wishlist.map(item => 
        typeof item === 'object' ? item.id : item
      ));
      
      const newItems = guestItems.filter(item => {
        const itemId = typeof item === 'object' ? item.id : item;
        return !existingIds.has(itemId);
      });
      
      // Combine and limit to max items
      this.wishlist = [...this.wishlist, ...newItems].slice(0, this.MAX_ITEMS);

      // Save to metafields
      await this.saveToMetafields();

      // Clear localStorage
      localStorage.removeItem(this.STORAGE_KEY);

      this.emit('migrated', { 
        guestItems: guestItems.length, 
        totalItems: this.wishlist.length 
      });
    } catch (error) {
      console.error('Failed to migrate guest wishlist:', error);
    }
  }

  /**
   * Generate shareable wishlist link
   */
  async generateShareLink() {
    try {
      const shareData = {
        items: this.wishlist,
        createdAt: new Date().toISOString()
      };

      // Encode wishlist data
      const encoded = btoa(JSON.stringify(shareData));
      
      // Generate share URL
      const shareUrl = `${window.location.origin}/pages/shared-wishlist?data=${encoded}`;
      
      this.trackAnalytics('wishlist_share');
      
      return shareUrl;
    } catch (error) {
      console.error('Failed to generate share link:', error);
      return null;
    }
  }

  /**
   * Load wishlist from share link
   */
  loadFromShareLink(encodedData) {
    try {
      const decoded = atob(encodedData);
      const shareData = JSON.parse(decoded);
      
      if (shareData.items && Array.isArray(shareData.items)) {
        return shareData.items;
      }
    } catch (error) {
      console.error('Failed to load wishlist from share link:', error);
    }
    return [];
  }

  /**
   * Set up periodic sync for authenticated users
   */
  setupPeriodicSync() {
    // Sync every 30 seconds if there are changes
    setInterval(() => {
      if (this.hasUnsavedChanges) {
        this.saveToMetafields();
        this.hasUnsavedChanges = false;
      }
    }, 30000);
  }

  /**
   * Normalize product ID
   */
  normalizeProductId(productId) {
    if (typeof productId === 'string') {
      // Remove Shopify GID prefix if present
      return productId.replace('gid://shopify/Product/', '');
    }
    return String(productId);
  }

  /**
   * Get customer token
   */
  getCustomerToken() {
    // Try to get customer access token from various sources
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('customer_access_token='))
      ?.split('=')[1];
    
    return token || '';
  }

  /**
   * Event emitter - addEventListener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Event emitter - removeEventListener
   */
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Event emitter - emit event
   */
  emit(event, data) {
    if (!this.listeners.has(event)) return;
    
    this.listeners.get(event).forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in wishlist event listener for ${event}:`, error);
      }
    });
  }

  /**
   * Track analytics
   */
  trackAnalytics(event, data = {}) {
    // Send to Shopify analytics
    if (window.Shopify?.analytics?.track) {
      window.Shopify.analytics.track(event, data);
    }

    // Send to Google Analytics if available
    if (window.gtag) {
      window.gtag('event', event, data);
    }

    // Custom analytics endpoint for logged-in users
    if (this.isAuthenticated) {
      fetch('/apps/wishlist/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ event, data })
      }).catch(error => console.error('Analytics tracking failed:', error));
    }
  }
}

// Create singleton instance
window.WishlistManager = window.WishlistManager || new WishlistManager();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = WishlistManager;
}